import React, { useEffect, useState } from 'react';
import { isIncentivizedCallsEnabled } from '../../../helpers/feature_checker';

import styles from './index.module.css';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Keep in sync with CallIncentive::DEFAULT_AMOUNT_CENTS.
export const DEFAULT_INCENTIVE_AMOUNT_CENTS = 1000;

// Returns the persisted incentive when the patient can still act on it, or
// null when the feature is off / the offer is expired or terminal.
export const activeCallIncentive = (account, conversation) => {
  if (!isIncentivizedCallsEnabled(account)) return null;

  const incentive = conversation?.call_incentive;
  if (!incentive) return null;

  if (incentive.status === 'pending_completion') return incentive;
  if (incentive.status !== 'offered') return null;

  return new Date(incentive.expires_at).getTime() > Date.now() ? incentive : null;
};

// What the UI should advertise. Prefers an active persisted offer (with
// countdown); otherwise, when the feature is on and no terminal/expired
// incentive exists yet, returns a default offered payload so the callout can
// show gift-card copy before OfferService has created a row.
export const displayCallIncentive = (account, conversation) => {
  if (!isIncentivizedCallsEnabled(account)) return null;

  const active = activeCallIncentive(account, conversation);
  if (active) return active;

  const existing = conversation?.call_incentive;
  if (existing) return null;

  return {
    status: 'offered',
    amount_cents: DEFAULT_INCENTIVE_AMOUNT_CENTS,
    expires_at: null,
  };
};

export const formatTimeRemaining = (expiresAt, nowMs = Date.now()) => {
  const remainingMs = new Date(expiresAt).getTime() - nowMs;
  if (remainingMs <= 0) return null;

  const days = Math.floor(remainingMs / DAY_MS);
  const hours = Math.floor((remainingMs % DAY_MS) / HOUR_MS);
  const minutes = Math.floor((remainingMs % HOUR_MS) / MINUTE_MS);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(minutes, 1)}m`;
};

// Re-renders every minute so the countdown stays fresh while the offer is up.
export const useIncentiveCountdown = (expiresAt) => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return undefined;
    const interval = setInterval(() => setNowMs(Date.now()), MINUTE_MS);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!expiresAt) return null;
  return formatTimeRemaining(expiresAt, nowMs);
};

export const incentiveAmountLabel = (incentive) => `$${Math.round((incentive?.amount_cents ?? 0) / 100)}`;

const offeredCopy = (variant, amount) => {
  switch (variant) {
    case 'callout':
      return (
        <>
          Earn a <strong>{amount} Amazon gift card</strong> by requesting or scheduling a call now
        </>
      );
    case 'schedule':
      return (
        <>
          Your <strong>{amount} Amazon gift card</strong> arrives after you and your mentor talk.
        </>
      );
    case 'request':
    default:
      return (
        <>
          Your <strong>{amount} Amazon gift card</strong> is yours whether you talk now or schedule for later.
        </>
      );
  }
};

const bannerCopy = (incentive, amount, name, variant) => {
  if (incentive.status === 'pending_completion') {
    return (
      <>
        Complete your call with <strong>{name}</strong> to earn your <strong>{amount} Amazon gift card</strong>.
      </>
    );
  }

  return offeredCopy(variant, amount);
};

// Compact banner advertising the gift card offer, reused across the call
// modals, the mobile calls drawer, the desktop profile panel, and the
// voice-call callout card.
export default function IncentiveBanner({ incentive, mentorName, className, inline = false, variant = 'request' }) {
  const countdown = useIncentiveCountdown(incentive?.status === 'offered' ? incentive?.expires_at : null);

  if (!incentive) return null;

  const amount = incentiveAmountLabel(incentive);
  const name = mentorName || 'your mentor';
  const bannerClass = [styles.banner, inline ? styles.bannerInline : null, className].filter(Boolean).join(' ');

  return (
    <div className={bannerClass} role='note'>
      <span className={styles.text}>{bannerCopy(incentive, amount, name, variant)}</span>
      {countdown && <span className={styles.timer}>Offer ends in {countdown}</span>}
    </div>
  );
}
